import { UserDto } from './dto/user.dto';
import { UserSearchDto } from './dto/user-search.dto';
import { UserSummaryDto } from './dto/user-summary.dto';
import { PagedResultGeneric } from '../../shared/model/paged-result.model';
import { Injectable } from '@angular/core';
import { UserRead } from './user-read.model';
import { IUserService } from './iuser.service';
import { ApiService, ApiCacheOptions, AuthService, UtilService, PagedSearch, PagedResult } from '../../shared/index';

import { UserSecurityElement } from './user-security-element.model';
import { UserServiceApiService } from 'src/app/shared/api/user-service-api.service';
import { TenantSelectionDto } from './dto/tenant-selection.dto';
@Injectable({
  providedIn: 'root',
})
export class UserService implements IUserService {
  public _securityElementCollection: Array<UserSecurityElement>;
  private _cachedUser: UserRead = null;
  private _cacheOptions: ApiCacheOptions;
  private _noCacheOption: ApiCacheOptions;

  public constructor(
    private _apiService: ApiService,
    private _authService: AuthService,
    private _utilService: UtilService,
    private _userServiceApiService: UserServiceApiService
  ) {
    this._securityElementCollection = new Array<UserSecurityElement>();
    this._cacheOptions = this._apiService.getDefaultApiCacheOptionsForInfrequentlyChangedData();
    this._noCacheOption = this._apiService.getDefaultApiCacheOptionsForNoCache();
  }

  public search(searchParams?: UserSearchDto, noCache?: boolean): Promise<PagedResultGeneric<UserSummaryDto>> {
    return this._userServiceApiService.postPaged('user/v2/tenantuser/search', searchParams);
  }

  public getActiveUsers(): Promise<Array<UserRead>> {
    return this._apiService.get('Users/ActiveUsers', this._cacheOptions);
  }

  public getCurrentUserLegacyToken(): Promise<string> {
    return this._authService.getUserInfo().then((data: any) => {
      let url = 'Security/User/' + data.wli_usernum.toString() + '/Token';
      return this._apiService.get(url);
    });
  }

  public getCurrentUser(): Promise<UserRead> {
    if (this._cachedUser) return Promise.resolve(this._cachedUser);

    return this._authService.getUserInfo().then((data: any) => {
      let url = 'User/' + data.wli_usernum.toString();
      return this._apiService.get(url).then((u) => {
        if (u) this._cachedUser = u;
        return u;
      });
    });
  }

  public getUserSecurityElements(userNum: number): Promise<any> {
    let url = 'User/' + userNum.toString() + '/Security/Elements';
    return this._apiService.get(url, this._cacheOptions);
  }

  public getUserSecurityElementAccessLevel(securityElement: string, userNum: number): Promise<number> {
    let url = 'User/' + userNum.toString() + '/Security/Elements';
    return this._apiService.get(url, this._cacheOptions).then((data: any) => {
      let accesslevel = 2;
      for (let element of data) {
        if (
          this._utilService.toLowerTrim(element.Element.EnumName) == this._utilService.toLowerTrim(securityElement) ||
          this._utilService.toLowerTrim(element.Element.DisplayName) == this._utilService.toLowerTrim(securityElement)
        ) {
          accesslevel = element.AccessLevel;
          break;
        }
      }
      return Promise.resolve(accesslevel);
    });
  }

  public getCurrentUserSecurityElementAccessLevel(securityElement: string): Promise<number> {
    return this.getCurrentUserSecurityElement(securityElement).then((data: UserSecurityElement) => {
      return Promise.resolve(data.AccessLevel);
    });
  }

  public getCurrentUserSecurityElements(): Promise<Array<UserSecurityElement>> {
    return this._authService.getUserInfo().then((data: any) => {
      let url = 'User/' + data.wli_usernum.toString() + '/Security/Elements';

      return this._apiService.get(url, this._cacheOptions);
    });
  }

  public getCurrentUserSecurityElement(elementName: string): Promise<UserSecurityElement> {
    if (this._securityElementCollection.length == 0) {
      return this.getCurrentUserSecurityElements().then((data: Array<UserSecurityElement>) => {
        this._securityElementCollection = data;
        let se = this.getSecurityElementFromCollection(elementName, this._securityElementCollection);
        return Promise.resolve(se);
      });
    } else {
      let se = this.getSecurityElementFromCollection(elementName, this._securityElementCollection);
      return Promise.resolve(se);
    }
  }

  public getSecurityElementFromCollection(securityElement: string, collection: Array<UserSecurityElement>): UserSecurityElement {
    let result = new UserSecurityElement();
    for (let element of collection) {
      if (
        this._utilService.toLowerTrim(securityElement) == this._utilService.toLowerTrim(element.Element.EnumName) ||
        this._utilService.toLowerTrim(securityElement) == this._utilService.toLowerTrim(element.Element.DisplayName)
      ) {
        result = element;
      }
    }

    return result;
  }

  public getUserFullName(id: number): Promise<string> {
    let url = `/User/${id}/FullName`;
    return this._apiService.get(url);
  }

  public get(userNum: number): Promise<UserDto> {
    return this._userServiceApiService.get(`user/v2/tenantuser/${userNum}`);
  }

  public create(user: UserDto): Promise<number> {
    return this._apiService.post(`Users/TenantAndAuthServer`, user);
  }

  public update(user: UserDto): Promise<void> {
    return this._apiService.put(`User/${user.UserNum}/TenantAndAuthServer`, user);
  }

  public upsert(user: UserDto): Promise<any> {
    return this._userServiceApiService.post(`user/v2/${user.UserNum ? user.UserNum : 0}/upsert`, user);
  }

  public getPersonifyTenantList(): Promise<TenantSelectionDto[]> {
    return this._userServiceApiService.get('user/v2/multi-tenant/authorized-tenants');
  }

  public upsertOktaUser(psfyIdpId: string): Promise<number> {
    return this._userServiceApiService.post(`user/v2/${psfyIdpId}/upsert-okta-user`, {});
  }
}
